import { Button } from '@heroui/react';
import React, { FC, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ContentLoadError, DataTable, PageTitle, PageWrapper } from '../../../../components';
import { Ad, useGetAds } from '../../../../queries';
import { AdColumns } from './columns';

const PAGE_SIZE = 20;

const matchesSearch = (ad: Ad, needle: string): boolean => {
  if (!needle) return true;
  return [ad.name, ad.type, ad.url].some((field) => field.toLowerCase().includes(needle));
};

export const Ads: FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Number(searchParams.get('page') ?? '1');
  const searchTerm = searchParams.get('q') ?? '';

  const { data: ads, error, isFetching, refetch } = useGetAds();

  const { pageData, totalPages } = useMemo(() => {
    const all = ads ?? [];
    const needle = searchTerm.trim().toLowerCase();
    const filtered = needle ? all.filter((ad) => matchesSearch(ad, needle)) : all;
    const total = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const safePage = Math.min(Math.max(1, currentPage), total);
    const start = (safePage - 1) * PAGE_SIZE;
    return { pageData: filtered.slice(start, start + PAGE_SIZE), totalPages: total };
  }, [ads, searchTerm, currentPage]);

  const handlePageChange = (page: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', String(page));
      return next;
    });
  };

  const handleSearchChange = (value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set('q', value);
      else next.delete('q');
      next.set('page', '1');
      return next;
    });
  };

  if (error) {
    return (
      <PageWrapper>
        <ContentLoadError message={error.message} />
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <PageTitle
        title="Reklamy"
        actions={(
          <Button
            size="sm"
            color="secondary"
            radius="sm"
            onPress={() => navigate('/ads/new')}
          >
            Nowa reklama
          </Button>
        )}
      />
      <DataTable
        data={pageData}
        columns={AdColumns}
        isLoading={isFetching}
        isError={!!error}
        onRefresh={refetch}
        pageSize={PAGE_SIZE}
        searchPlaceholder="Szukaj po nazwie, typie, linku..."
        totalPages={totalPages}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        searchValue={searchTerm}
        onSearchChange={handleSearchChange}
      />
    </PageWrapper>
  );
};
